<section class="workflow-editor">
  @if (workflow.error) {
    <p class="message error">{{ workflow.error }}</p>
  }
  @if (parseError; as errorReport) {
    <div class="message error json-error-report">
      <strong>{{ errorReport.title }}</strong>
      <ul>
        @for (detail of jsonErrorDetails(errorReport); track detail) {
          <li>{{ detail }}</li>
        }
      </ul>
    </div>
  }

  <div class="workflow-header">
    <div>
      <h2>{{ t('HubWorkflow') }}</h2>
      <p>{{ t('HubWorkflowIntro') }}</p>
    </div>
    <div class="workflow-actions-stack">
      @if (modifiedDate(workflow.lastModified); as modified) {
        <span>{{ t('HubLastChanged') }} {{ modified | date:'d MMM y, HH:mm' }}</span>
      }
      <div class="workflow-toolbar">
        <span class="autosave-status">{{ autosaveStatus }}</span>
        <div class="undo-redo-controls" role="group" [attr.aria-label]="t('HubUndoRedo')">
          <button class="secondary-button icon-button" type="button" (click)="requestUndo()" [disabled]="!canUndo" [attr.disabled]="!canUndo ? '' : null" [title]="t('HubUndo')" [attr.aria-label]="t('HubUndo')"><wa-icon name="arrow-counterclockwise"></wa-icon></button>
          <button class="secondary-button icon-button" type="button" (click)="requestRedo()" [disabled]="!canRedo" [attr.disabled]="!canRedo ? '' : null" [title]="t('HubRedo')" [attr.aria-label]="t('HubRedo')"><wa-icon name="arrow-clockwise"></wa-icon></button>
        </div>
        <div class="view-toggle" role="group" [attr.aria-label]="t('HubEditorViewToggle')">
          <button class="view-toggle-button" type="button" [class.active]="mode === 'gui'" (click)="mode !== 'gui' && toggleMode()" [title]="t('HubShowGui')" [attr.aria-label]="t('HubShowGui')"><wa-icon name="diagram-3"></wa-icon></button>
          <button class="view-toggle-button" type="button" [class.active]="mode === 'json'" (click)="mode !== 'json' && toggleMode()" [title]="t('HubShowJson')" [attr.aria-label]="t('HubShowJson')"><wa-icon name="braces"></wa-icon></button>
        </div>
      </div>
    </div>
  </div>

  @if (mode === 'gui' && visualReady) {
    <div class="workflow-layout">
      <div class="workflow-canvas-card">
        <div class="workflow-canvas-toolbar">
          <button class="secondary-button" type="button" (click)="addTask()"><wa-icon name="plus-square"></wa-icon>{{ t('HubWorkflowAddTask') }}</button>
          <button class="secondary-button" type="button" (click)="addEvent('start')"><wa-icon name="play-circle"></wa-icon>{{ t('HubWorkflowAddStartEvent') }}</button>
          <button class="secondary-button" type="button" (click)="addEvent('end')"><wa-icon name="stop-circle"></wa-icon>{{ t('HubWorkflowAddEndEvent') }}</button>
          <button class="secondary-button" type="button" (click)="addGateway()"><wa-icon name="diamond"></wa-icon>{{ t('HubWorkflowAddGateway') }}</button>
          <button class="secondary-button" type="button" (click)="downloadSvg()"><wa-icon name="download"></wa-icon>{{ t('HubWorkflowDownloadSvg') }}</button>
          <div class="zoom-controls" role="group" [attr.aria-label]="t('HubWorkflowZoom')">
            <button class="secondary-button compact-button" type="button" (click)="zoomOut()" [disabled]="zoom <= minZoom" [title]="t('HubWorkflowZoomOut')" [attr.aria-label]="t('HubWorkflowZoomOut')"><wa-icon name="dash-lg"></wa-icon></button>
            <button class="secondary-button zoom-reset-button" type="button" (click)="resetZoom()">{{ zoomPercent() }}</button>
            <button class="secondary-button compact-button" type="button" (click)="zoomIn()" [disabled]="zoom >= maxZoom" [title]="t('HubWorkflowZoomIn')" [attr.aria-label]="t('HubWorkflowZoomIn')"><wa-icon name="plus-lg"></wa-icon></button>
          </div>
        </div>
        <svg #canvas class="workflow-canvas" [ngStyle]="scaledCanvasStyle()" [attr.viewBox]="canvasViewBox()" (pointerdown)="startSelectionBox($event)">
          <defs>
            <filter id="workflow-selected-shadow" x="-25%" y="-25%" width="150%" height="150%">
              <feDropShadow dx="0" dy="8" stdDeviation="5" flood-color="#172033" flood-opacity="0.25"></feDropShadow>
            </filter>
          </defs>
          <g class="connections">
            @for (connection of model.connections; track trackConnection($index, connection)) {
              <g class="workflow-connection" [class.selected]="isSelected(connection.id)" (pointerdown)="selectElement(connection.id, $event)">
                @if (connectionGeometry(connection); as geometry) {
                  <ng-container>
                    <path class="connection-hit" [attr.d]="geometry.path"></path>
                    <path class="connection-line" [attr.d]="geometry.path" [attr.stroke]="connectionColor(connection)"></path>
                  </ng-container>
                }
              </g>
            }
            @if (previewConnectionGeometry(); as preview) {
              <ng-container>
                <g class="connection-preview-line">
                  <path class="connection-line" [attr.d]="preview.path" [attr.stroke]="previewConnectionColor()"></path>
                </g>
              </ng-container>
            }
          </g>
          <g class="nodes">
            @for (node of nodes(); track trackNode($index, node)) {
              <g class="workflow-node" [class.selected]="isSelected(node.id)" [class.task-node]="node.kind === 'task'" [class.event-node]="node.kind === 'event'" [class.gateway-node]="node.kind === 'gateway'" [attr.transform]="'translate(' + node.item.pos.x + ' ' + node.item.pos.y + ')'" (pointerdown)="startNodeDrag($event, node)">
                <ng-container>
                  @switch (node.kind) {
                    @case ('task') {
                      <g>
                        <rect class="connection-target node-outer-shape" [attr.width]="taskSize.width" [attr.height]="taskSize.height" rx="20" (pointerdown)="startNewConnection($event, node, attachmentFromLocalPointer(node, pointerPosition($event)))"></rect>
                        <rect class="node-shape" [attr.x]="nodeInnerBox(node).x" [attr.y]="nodeInnerBox(node).y" [attr.width]="nodeInnerBox(node).width" [attr.height]="nodeInnerBox(node).height" rx="14"></rect>
                        <text class="node-title" x="22" y="34">{{ shortText(nodeTitle(node), 22) }}</text>
                        <text class="node-subtitle" x="22" y="57">{{ shortText(nodeSubtitle(node), 24) }}</text>
                      </g>
                    }
                    @case ('event') {
                      <g [class.start-event-node]="eventType(node) === 'start'" [class.end-event-node]="eventType(node) === 'end'">
                        <rect class="connection-target node-outer-shape" [attr.width]="eventSize.width" [attr.height]="eventSize.height" rx="12" (pointerdown)="startNewConnection($event, node, attachmentFromLocalPointer(node, pointerPosition($event)))"></rect>
                        <circle class="node-shape" [attr.cx]="eventSize.width / 2" [attr.cy]="eventSize.height / 2" [attr.r]="eventSize.width / 2 - 10"></circle>
                        <text class="event-label" [attr.x]="eventSize.width / 2" [attr.y]="eventSize.height / 2 + 5">{{ nodeTitle(node) }}</text>
                      </g>
                    }
                    @case ('gateway') {
                      <g>
                        <rect class="connection-target node-outer-shape" [attr.width]="gatewaySize.width" [attr.height]="gatewaySize.height" rx="12" (pointerdown)="startNewConnection($event, node, attachmentFromLocalPointer(node, pointerPosition($event)))"></rect>
                        <rect class="node-shape gateway-shape" [attr.x]="gatewaySize.width / 2 - 24" [attr.y]="gatewaySize.height / 2 - 24" width="48" height="48" transform="rotate(45 37 37)"></rect>
                        <text class="event-label" [attr.x]="gatewaySize.width / 2" [attr.y]="gatewaySize.height / 2 + 5">X</text>
                      </g>
                    }
                  }
                </ng-container>
              </g>
            }
          </g>
          <g class="snap-guides">
            @if (snapGuides.vertical !== null) {
              <line [attr.x1]="snapGuides.vertical" y1="0" [attr.x2]="snapGuides.vertical" [attr.y2]="canvasSize().height"></line>
            }
            @if (snapGuides.horizontal !== null) {
              <line x1="0" [attr.y1]="snapGuides.horizontal" [attr.x2]="canvasSize().width" [attr.y2]="snapGuides.horizontal"></line>
            }
          </g>
          @if (selectionBox(); as box) {
            <rect class="selection-box" [attr.x]="box.x" [attr.y]="box.y" [attr.width]="box.width" [attr.height]="box.height"></rect>
          }
          <g class="connection-overlays">
            @for (connection of model.connections; track trackConnection($index, connection)) {
              <g class="workflow-connection" [class.selected]="isSelected(connection.id)">
                @if (connectionGeometry(connection); as geometry) {
                  <ng-container>
                    <circle class="connection-start-hit" [attr.cx]="geometry.start.x" [attr.cy]="geometry.start.y" r="13" (pointerdown)="startReattachConnection($event, connection, 'from')"></circle>
                    <path class="connection-arrow-hit" [attr.d]="geometry.arrowPath" (pointerdown)="startReattachConnection($event, connection, 'to')"></path>
                    <circle class="connection-start-handle" [attr.cx]="geometry.start.x" [attr.cy]="geometry.start.y" r="6" [attr.fill]="connectionColor(connection)"></circle>
                    <path class="connection-arrow-handle" [attr.d]="geometry.arrowPath"></path>
                    <text class="connection-label" [attr.x]="geometry.label.x" [attr.y]="geometry.label.y - 10">{{ connectionTypeLabel(connection.connectionType) }}</text>
                  </ng-container>
                }
              </g>
            }
            @if (previewConnectionGeometry(); as preview) {
              <ng-container>
                <g class="connection-preview">
                  <circle class="connection-start-handle" [attr.cx]="preview.start.x" [attr.cy]="preview.start.y" r="6" [attr.fill]="previewConnectionColor()"></circle>
                  <path class="connection-arrow-handle" [attr.d]="preview.arrowPath"></path>
                </g>
              </ng-container>
            }
          </g>
        </svg>
      </div>
      <aside class="workflow-inspector card">
        <div class="inspector-header">
          <div>
            <h3>{{ hasSelection() ? selectedElementLabel() : t('HubWorkflowInspector') }}</h3>
            <p>{{ isMultiSelection() ? selectedIds.size + ' ' + t('HubWorkflowSelectedItems') : selectedId ? selectedId : t('HubWorkflowInspectorEmpty') }}</p>
          </div>
          @if (hasSelection()) {
            <button class="danger-button icon-button" type="button" (click)="deleteSelected()" [title]="t('Delete')" [attr.aria-label]="t('Delete')"><wa-icon name="trash"></wa-icon></button>
          }
        </div>
        @if (isMultiSelection()) {
          <div class="selection-stats">
            @for (stat of selectionStats(); track stat) {
              <div>
                <strong>{{ stat.value }}</strong>
                <span>{{ stat.label }}</span>
              </div>
            }
          </div>
        }
        @if (!isMultiSelection() && selectedTask(); as task) {
          <div class="form-grid inspector-form">
            <label><span class="field-label">{{ t('HubWorkflowTaskName') }} <hub-help-tooltip [text]="t('HubWorkflowTaskNameHelp')"></hub-help-tooltip></span><input name="workflowTaskName" [(ngModel)]="task.name" (ngModelChange)="updateNode()"></label>
            <label><span class="field-label">{{ t('HubWorkflowTaskProgress') }} <hub-help-tooltip [text]="t('HubWorkflowTaskProgressHelp')"></hub-help-tooltip></span><input name="workflowTaskProgress" [(ngModel)]="task.progress" (ngModelChange)="updateNode()"></label>
            <label><span class="field-label">{{ t('HubWorkflowTaskType') }} <hub-help-tooltip [text]="t('HubWorkflowTaskTypeHelp')"></hub-help-tooltip></span><select name="workflowTaskType" [(ngModel)]="task.taskType" (ngModelChange)="updateNode()">@for (taskType of taskTypes; track taskType) {
            <option [value]="taskType">{{ taskType }}</option>
          }</select></label>
          <label><span class="field-label">{{ t('HubWorkflowTaskScreen') }} <hub-help-tooltip [text]="t('HubWorkflowTaskScreenHelp')"></hub-help-tooltip></span><select name="workflowTaskScreen" [(ngModel)]="task.screen" (ngModelChange)="updateNode()"><option value="">{{ t('HubWorkflowNoScreen') }}</option>@for (screen of taskScreenOptions(task); track screen) {
          <option [value]="screen">{{ screen }}</option>
        }</select></label>
        <div class="position-grid">
          <label><span class="field-label">X <hub-help-tooltip [text]="t('HubWorkflowTaskXHelp')"></hub-help-tooltip></span><input name="workflowTaskX" type="number" [(ngModel)]="task.pos.x" (ngModelChange)="updateNode()"></label>
          <label><span class="field-label">Y <hub-help-tooltip [text]="t('HubWorkflowTaskYHelp')"></hub-help-tooltip></span><input name="workflowTaskY" type="number" [(ngModel)]="task.pos.y" (ngModelChange)="updateNode()"></label>
        </div>
      </div>
    }
    @if (!isMultiSelection() && selectedEvent(); as event) {
      <div class="form-grid inspector-form">
        <label>{{ t('HubWorkflowEventType') }}<select name="workflowEventType" [(ngModel)]="event.eventType" (ngModelChange)="updateNode()"><option value="start">{{ t('HubWorkflowStartEvent') }}</option><option value="end">{{ t('HubWorkflowEndEvent') }}</option></select></label>
        <div class="position-grid">
          <label>X<input name="workflowEventX" type="number" [(ngModel)]="event.pos.x" (ngModelChange)="updateNode()"></label>
          <label>Y<input name="workflowEventY" type="number" [(ngModel)]="event.pos.y" (ngModelChange)="updateNode()"></label>
        </div>
      </div>
    }
    @if (!isMultiSelection() && selectedGateway(); as gateway) {
      <div class="form-grid inspector-form">
        <label>{{ t('HubWorkflowGatewayType') }}<input name="workflowGatewayType" [(ngModel)]="gateway.type" (ngModelChange)="updateNode()"></label>
        <div class="position-grid">
          <label>X<input name="workflowGatewayX" type="number" [(ngModel)]="gateway.pos.x" (ngModelChange)="updateNode()"></label>
          <label>Y<input name="workflowGatewayY" type="number" [(ngModel)]="gateway.pos.y" (ngModelChange)="updateNode()"></label>
        </div>
      </div>
    }
    @if (!isMultiSelection() && selectedConnection(); as connection) {
      <div class="form-grid inspector-form">
        <label>{{ t('HubWorkflowConnectionType') }}<select name="workflowConnectionType" [(ngModel)]="connection.connectionType" (ngModelChange)="updateConnection()">@for (type of connectionTypes; track type) {
          <option [value]="type.value">{{ t(type.labelKey) }}</option>
        }</select></label>
        <label>{{ t('HubWorkflowConnectionFrom') }}<select name="workflowConnectionFrom" [(ngModel)]="connection.from" (ngModelChange)="updateConnection()">@for (node of nodes(); track node) {
          <option [value]="node.id">{{ nodeDisplayName(node.id) }}</option>
        }</select></label>
        <label>{{ t('HubWorkflowConnectionTo') }}<select name="workflowConnectionTo" [(ngModel)]="connection.to" (ngModelChange)="updateConnection()">@for (node of nodes(); track node) {
          <option [value]="node.id">{{ nodeDisplayName(node.id) }}</option>
        }</select></label>
        <label>{{ t('HubWorkflowConnectionScript') }} <hub-help-tooltip [text]="t('HubWorkflowConnectionScriptHelp')"></hub-help-tooltip><textarea name="workflowConnectionScript" [(ngModel)]="connection.connectionScript" (ngModelChange)="updateConnection()" placeholder="$amount > 0"></textarea></label>
        <label>{{ t('HubWorkflowConnectionEnabledCondition') }} <hub-help-tooltip [text]="t('HubWorkflowConnectionEnabledConditionHelp')"></hub-help-tooltip><input name="workflowConnectionEnabledCondition" [(ngModel)]="connection.enabledCondition" (ngModelChange)="updateConnection()" placeholder="true"></label>
        <p class="empty-state">{{ t('HubWorkflowReattachHelp') }}</p>
      </div>
    }
  </aside>
</div>
} @else {
  <div class="raw-json-panel">
    <div class="raw-json-header">
      <div>
        <h3>{{ t('HubRawJson') }}</h3>
        <p>{{ t('HubWorkflowRawJsonIntro') }}</p>
      </div>
      <button class="secondary-button" type="button" (click)="applyJson()">{{ t('HubApplyJson') }}</button>
    </div>
    <textarea class="json-editor" name="workflowJson" [(ngModel)]="workflow.json" (ngModelChange)="onJsonChange()"></textarea>
  </div>
}

</section>
